import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  FocusableComponentLayout,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation'
import tts from '../../lib/TTS'
import FocusableLink from '../../components/FocusableLink'
import type { AboutLinkProps, ModalType, SettingsAboutProps } from './types'

const TITLE = 'About'

const LINKS: AboutLinkProps[] = [
  {
    key: 'privacyPolicyModal',
    label: 'Privacy Policy',
    modalType: 'privacy',
  },
  {
    key: 'termsOfUseModal',
    label: 'Terms of Use',
    modalType: 'terms',
  },
  {
    key: 'closedCaptionModal',
    label: 'Closed Caption Support',
    modalType: 'captions',
  },
]

const SettingsAbout = ({
  handleFocus,
  setModalType,
  isModalOpen,
}: SettingsAboutProps) => {
  const previousModalTypeRef = useRef<ModalType | null>(null)
  const [hasAnnouncedTitle, setHasAnnouncedTitle] = useState(false)

  const announceFocusWithTitle = useCallback(
    (label: string) => {
      tts.stopRead()

      if (!hasAnnouncedTitle) {
        tts.addTextToRead(`${TITLE}. Focused on ${label}.`)
        setHasAnnouncedTitle(true)
      } else {
        tts.addTextToRead(`Focused on ${label}.`)
      }
    },
    [hasAnnouncedTitle]
  )

  const handleLinkFocus = useCallback(
    (label: string) => (layout: FocusableComponentLayout) => {
      announceFocusWithTitle(label)
      handleFocus?.(layout)
    },
    [announceFocusWithTitle, handleFocus]
  )

  const handleEnterPress = useCallback(
    (modalType: ModalType) => {
      previousModalTypeRef.current = modalType
      setHasAnnouncedTitle(false)
      setModalType(modalType)
    },
    [setModalType]
  )

  // Restore focus when modal closes
  useEffect(() => {
    if (!isModalOpen && previousModalTypeRef.current) {
      const link = LINKS.find(
        (l) => l.modalType === previousModalTypeRef.current
      )

      if (link) {
        setFocus(link.key)
        announceFocusWithTitle(link.label)
      }

      previousModalTypeRef.current = null
    }
  }, [isModalOpen, announceFocusWithTitle])

  return (
    <div className="settings-section-container">
      <h2>{TITLE}</h2>

      {LINKS.map(({ key, label, modalType }) => (
        <FocusableLink
          key={key}
          focusKey={key}
          label={label}
          onFocus={handleLinkFocus(label)}
          onEnterPress={() => handleEnterPress(modalType)}
          disableTTS={true}
        >
          <div className="about">
            <h3>{label}</h3>
          </div>
        </FocusableLink>
      ))}
    </div>
  )
}

export default SettingsAbout
